import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import { FaCirclePlus } from "react-icons/fa6";

const StyleCard = memo(function StyleCard({ data, width: nodeWidth, height: nodeHeight, onUpdateData, onUpdateField, onUpdateEditingState, onSelectNode, currentUser, nodeId, selected }) {
  const width = data?.width || nodeWidth || 260;
  const height = data?.height || nodeHeight || 260;
  const {
    brd_cd, part_cd, sesn, item, description, fabric, fiber_content, domain, sub_domain, set_up_style_no, life_style, level, fit, price,
    colors = [], designer, vendor, image, mapType, color_count, expected_qty,
    // 판매맵 데이터
    tag_price, unit_cost, ac_ord_qty_kor, ac_sale_qty_cns, cns_delv_dt_1st, style_sale_rate,
    w1_cns, w2_cns, w3_cns, salesData = [],
    // 로딩 상태
    isLoading = false,
  } = data || {};

  const [isEditingPartCode, setIsEditingPartCode] = useState(false);
  const descRef = useRef(null);
  useEffect(() => {
    const el = descRef.current;
    if (!el) return;
    el.style.fontSize = '10px';
    let size = 10;
    while (el.scrollWidth > el.offsetWidth && size > 6) {
      size -= 0.5;
      el.style.fontSize = size + 'px';
    }
  }, [description]);
  const [inputPartCode, setInputPartCode] = useState(part_cd || '');
  const blurHandledRef = useRef(false);
  const partCodeInputRef = useRef(null);

  // 다른 노드 클릭 시 ReactFlow가 mousedown에서 preventDefault()를 호출해 blur가 발생하지 않는 문제 해결
  // document capture phase에서 클릭 감지 → 직접 저장 처리
  const savePartCodeFnRef = useRef(null);
  savePartCodeFnRef.current = () => {
    if (blurHandledRef.current) {
      blurHandledRef.current = false;
      return;
    }
    blurHandledRef.current = true; // onBlur 중복 저장 방지
    setIsEditingPartCode(false);
    if (onUpdateEditingState) onUpdateEditingState(false);
    if (inputPartCode !== part_cd) {
      setInputPartCode('');
      if (mapType === '레인지맵' && onUpdateField) {
        onUpdateField(nodeId, 'part_cd', inputPartCode || '');
      } else if (onUpdateData) {
        onUpdateData(nodeId, inputPartCode || '');
      }
    }
  };
  useEffect(() => {
    if (!isEditingPartCode) return;
    const handleDocMouseDown = (e) => {
      if (partCodeInputRef.current && !partCodeInputRef.current.contains(e.target)) {
        savePartCodeFnRef.current?.();
      }
    };
    document.addEventListener('mousedown', handleDocMouseDown, true);
    return () => document.removeEventListener('mousedown', handleDocMouseDown, true);
  }, [isEditingPartCode]);
  const [imageError, setImageError] = useState(false);
  const [fallbackError, setFallbackError] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const nodeRef = useRef(null);
  // 레인지맵 필드 편집 상태
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const scrollTableRef = useCallback((el) => {
    if (!el) return;
    const stop = (e) => e.stopPropagation();
    el.addEventListener('wheel', stop, { passive: true });
    el.addEventListener('pointerdown', stop);
    el.addEventListener('pointermove', stop);
  }, []);

  const handlePartCodeChange = (e) => {
    setInputPartCode(e.target.value);
  };

  const handlePartCodeBlur = () => {
    // Enter 키로 이미 처리된 경우 blur 이벤트 중복 처리 방지
    if (blurHandledRef.current) {
      blurHandledRef.current = false;
      return;
    }
    setIsEditingPartCode(false);
    // 편집 종료 시 편집 상태 전송
    if (onUpdateEditingState) {
      onUpdateEditingState(false);
    }
    // 품번이 변경되었거나 삭제된 경우 업데이트
    if (inputPartCode !== part_cd && onUpdateData) {
      setInputPartCode(''); // 즉시 초기화 (useEffect가 part_cd 무변경 시 fire 안될 경우 대비)
      onUpdateData(nodeId, inputPartCode || '');
    }
  };

  const handlePartCodeKeyDown = (e) => {
    e.stopPropagation(); // ReactFlow 키보드 이벤트 전파 방지
    if (e.key === 'Enter') {
      blurHandledRef.current = true;
      setIsEditingPartCode(false);
      if (onUpdateEditingState) {
        onUpdateEditingState(false);
      }
      if (inputPartCode !== part_cd && onUpdateData) {
        setInputPartCode(''); // 즉시 초기화
        onUpdateData(nodeId, inputPartCode || '');
      }
    } else if (e.key === 'Escape') {
      setIsEditingPartCode(false);
      setInputPartCode(part_cd || '');
      // 편집 취소 시에도 편집 상태 전송
      if (onUpdateEditingState) {
        onUpdateEditingState(false);
      }
    }
  };

  // 레인지맵 필드 편집 핸들러
  const handleFieldClick = (fieldName, currentValue) => {
    // 이미 선택된 상태가 아닐 때만 노드 선택 (불필요한 리렌더 방지)
    if (onSelectNode && !selected) {
      onSelectNode();
    }
    // 품번 편집 중이면 먼저 저장 후 종료
    if (isEditingPartCode) {
      if (inputPartCode !== part_cd && onUpdateField) {
        onUpdateField(nodeId, 'part_cd', inputPartCode || '');
      }
      setIsEditingPartCode(false);
    }
    // 다른 필드 편집 중이면 먼저 저장 후 종료
    if (editingField && editingField !== fieldName && onUpdateField) {
      onUpdateField(nodeId, editingField, editingValue);
    }
    setEditingField(fieldName);
    setEditingValue(currentValue || '');
    if (onUpdateEditingState) {
      onUpdateEditingState(true);
    }
  };

  const handleFieldChange = (e) => {
    setEditingValue(e.target.value);
  };

  const handleFieldBlur = () => {
    if (editingField && onUpdateField) {
      onUpdateField(nodeId, editingField, editingValue);
    }
    setEditingField(null);
    setEditingValue('');
    if (onUpdateEditingState) {
      onUpdateEditingState(false);
    }
  };

  const handleFieldKeyDown = (e) => {
    e.stopPropagation(); // ReactFlow 키보드 이벤트 전파 방지
    if (e.key === 'Enter') {
      handleFieldBlur();
    } else if (e.key === 'Escape') {
      setEditingField(null);
      setEditingValue('');
      if (onUpdateEditingState) {
        onUpdateEditingState(false);
      }
    }
  };

  // 레인지맵 품번 편집 핸들러 (직접 입력용)
  const handleRangeMapPartCodeBlur = () => {
    setIsEditingPartCode(false);
    if (onUpdateEditingState) {
      onUpdateEditingState(false);
    }
    // 레인지맵은 품번도 직접 입력
    if (inputPartCode !== part_cd && onUpdateField) {
      onUpdateField(nodeId, 'part_cd', inputPartCode || '');
    }
  };

  const handleRangeMapPartCodeKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleRangeMapPartCodeBlur();
    } else if (e.key === 'Escape') {
      setIsEditingPartCode(false);
      setInputPartCode(part_cd || '');
      if (onUpdateEditingState) {
        onUpdateEditingState(false);
      }
    }
  };

  // 편집 가능한 필드 컴포넌트
  const EditableField = ({ fieldName, value, placeholder, style = {} }) => {
    if (editingField === fieldName) {
      return (
        <input
          type="text"
          className="nodrag nopan"
          value={editingValue}
          onChange={handleFieldChange}
          onBlur={handleFieldBlur}
          onKeyDown={handleFieldKeyDown}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          autoFocus
          style={{
            width: '60px',
            padding: '1px 2px',
            border: '1px solid #9CA3AF',
            borderRadius: 2,
            fontSize: 10,
            outline: 'none',
            pointerEvents: 'auto',
            ...style
          }}
        />
      );
    }
    return (
      <span
        className="nodrag nopan"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        onPointerDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
          handleFieldClick(fieldName, value);
        }}
        style={{
          cursor: 'pointer',
          color: value ? '#000' : '#9CA3AF',
          pointerEvents: 'auto',
          ...style
        }}
        title="클릭하여 입력"
      >
        {value || placeholder}
      </span>
    );
  };

  useEffect(() => {
    setInputPartCode(part_cd || '');
  }, [part_cd]);

  useEffect(() => {
    setImageError(false);
    setFallbackError(false);
  }, [image]);

  const fallbackImageUrl = brd_cd && sesn && part_cd
    ? `https://static-dashff.fnf.co.kr/interstellar/cad_img/${brd_cd}/${sesn}/${part_cd}.jpg`
    : null;

  // 판매맵 레이아웃
  if (mapType === '판매맵') {
    const salesIsFemale = part_cd && part_cd.length >= 2 && (part_cd[1] === 'F' || part_cd[1] === 'W' || part_cd[1] === 'D');
    const salesCardBorder = salesIsFemale ? (selected ? "3px solid #e879a3" : "1px solid #f9cade") : (selected ? "3px solid #A89F91" : "1px solid #ccc5b9");
    const salesHeaderBg = salesIsFemale ? '#F9CADE4F' : '#CCC5B94F';
    return (
      <>
        <div ref={nodeRef} style={{ width: width || 260, height: height || 260, position: "relative" }}>
          <div style={{ border: salesCardBorder, borderRadius: 8, background: "#fff", fontFamily: "Inter, ui-sans-serif", overflow: "clip", boxSizing: "border-box", width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
            {/* 로딩 오버레이 */}
            {isLoading && (
              <div style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(255, 255, 255, 0.85)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
                borderRadius: 8
              }}>
                <div style={{
                  width: 28,
                  height: 28,
                  border: "3px solid #E5E7EB",
                  borderTop: "3px solid #A89F91",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite"
                }} />
                <span style={{ marginTop: 8, fontSize: 11, color: "#6B7280" }}>데이터 조회 중...</span>
                <style>
                  {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
                </style>
              </div>
            )}
            {/* 헤더: 스타일품번 | 스타일명 */}
            <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 8px", background: salesHeaderBg, fontSize: 10 }}>
              {isEditingPartCode ? (
                <input
                  ref={partCodeInputRef}
                  type="text"
                  className="nodrag nopan"
                  value={inputPartCode}
                  onChange={handlePartCodeChange}
                  onBlur={handlePartCodeBlur}
                  onKeyDown={handlePartCodeKeyDown}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  autoFocus
                  style={{
                    width: '80px',
                    padding: '2px 4px',
                    border: '1px solid #9CA3AF',
                    borderRadius: 4,
                    fontSize: 12,
                    fontWeight: 700,
                    outline: 'none'
                  }}
                />
              ) : (
                <span
                  className="nodrag nopan"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onSelectNode) onSelectNode();
                    setIsEditingPartCode(true);
                    if (onUpdateEditingState) {
                      onUpdateEditingState(true);
                    }
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  style={{
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontWeight: 700
                  }}
                  title="클릭하여 스타일 품번 입력"
                >
                  {part_cd || '스타일품번'}
                </span>
              )}
              <span ref={descRef} style={{ color: description ? '#000' : '#9CA3AF', fontSize: 10, maxWidth: 150, overflow: 'hidden', whiteSpace: 'nowrap' }} title={description}>{description || '스타일명'}</span>
            </div>

            {/* 판매 정보 그리드 */}
            {(() => {
              const labelBg = salesIsFemale ? '#F9CADE1A' : '#CCC5B91A';

              return (
                <div style={{ padding: "4px 8px", fontSize: 9 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "2px", marginBottom: 2 }}>
                    <span style={{ color: '#6B7280', background: labelBg, padding: "1px 2px" }}>판매가</span>
                    <span style={{ color: tag_price ? '#000' : '#9CA3AF' }}>{tag_price ? tag_price.toLocaleString() : '-'}</span>
                    <span style={{ color: '#6B7280', background: labelBg, padding: "1px 2px" }}>원가(+VAT)</span>
                    <span style={{ color: unit_cost ? '#000' : '#9CA3AF' }}>{unit_cost ? unit_cost.toLocaleString() : '-'}</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "2px", marginBottom: 2 }}>
                    <span style={{ color: '#6B7280', background: labelBg, padding: "1px 2px" }}>발주량</span>
                    <span style={{ color: ac_ord_qty_kor ? '#000' : '#9CA3AF' }}>{ac_ord_qty_kor ? ac_ord_qty_kor.toLocaleString() : '-'}</span>
                    <span style={{ color: '#6B7280', background: labelBg, padding: "1px 2px" }}>판매량</span>
                    <span style={{ color: ac_sale_qty_cns ? '#000' : '#9CA3AF' }}>{ac_sale_qty_cns ? ac_sale_qty_cns.toLocaleString() : '-'}</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "2px", marginBottom: 2 }}>
                    <span style={{ color: '#6B7280', background: labelBg, padding: "1px 2px" }}>최초 출고</span>
                    <span style={{ color: cns_delv_dt_1st ? '#000' : '#9CA3AF' }}>{cns_delv_dt_1st || '-'}</span>
                    <span style={{ color: '#6B7280', background: labelBg, padding: "1px 2px" }}>판매율</span>
                    <span style={{ color: style_sale_rate ? '#000' : '#9CA3AF' }}>{style_sale_rate ? `${style_sale_rate}%` : '-'}</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr", gap: 0 }}>
                    <span style={{ color: '#6B7280', background: labelBg, padding: "1px 2px" }}>1주전</span>
                    <span style={{ color: w1_cns ? '#000' : '#9CA3AF', background: labelBg, padding: "1px 2px" }}>{w1_cns != null ? w1_cns.toLocaleString() : '-'}</span>
                    <span style={{ color: '#6B7280', background: labelBg, padding: "1px 2px" }}>2주전</span>
                    <span style={{ color: w2_cns ? '#000' : '#9CA3AF', background: labelBg, padding: "1px 2px" }}>{w2_cns != null ? w2_cns.toLocaleString() : '-'}</span>
                    <span style={{ color: '#6B7280', background: labelBg, padding: "1px 2px" }}>3주전</span>
                    <span style={{ color: w3_cns ? '#000' : '#9CA3AF', background: labelBg, padding: "1px 2px" }}>{w3_cns != null ? w3_cns.toLocaleString() : '-'}</span>
                  </div>
                </div>
              );
            })()}

            {/* 이미지 + 컬러 데이터 스크롤 영역 */}
            <div className="nodrag nopan" style={{ flex: 1, overflowY: "hidden", overflowX: "hidden", minHeight: 0 }}>
            {/* 컬러 데이터 테이블 (이미지 포함) */}
            {salesData && salesData.length > 0 ? (
              <div className="nodrag nopan" onClick={() => { if (onSelectNode) onSelectNode(); }} style={{ display: "flex", alignItems: "stretch", padding: "2px 4px 4px" }}>
                {(() => {
                  const labelBg = salesIsFemale ? '#fef7fb' : 'rgb(250 249 247)';
                  const headerBg = salesIsFemale ? '#fce4ef' : 'rgb(238 235 230)';
                  const needsScroll = salesData.length > 6;
                  const tableMinWidth = needsScroll ? `${(salesData.length / 6 * 100).toFixed(1)}%` : '100%';
                  const H = 14;
                  const imgH = 60;
                  const cellStyle = { fontSize: 8, lineHeight: '8px', padding: '2px 2px', borderBottom: '1px solid #e5e7eb' };
                  const labelCellStyle = { height: H, boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, whiteSpace: 'nowrap', borderBottom: '1px solid #e5e7eb' };
                  const imgUrls = salesData.map(item => item.image || fallbackImageUrl || null);
                  const uniqueImgs = [...new Set(imgUrls.filter(Boolean))];
                  const allSameImage = uniqueImgs.length <= 1;
                  const singleImgUrl = uniqueImgs[0] || null;
                  return (
                    <>
                      {/* 고정 라벨 컬럼 */}
                      <div style={{ flexShrink: 0, width: 44, display: 'flex', flexDirection: 'column', marginRight: 1 }}>
                        <div style={{ height: imgH, flexShrink: 0 }} />
                        <div style={{ height: H, flexShrink: 0, background: headerBg, borderBottom: '1px solid #e5e7eb', boxSizing: 'border-box' }} />
                        {['발주수량','입고수량','판매수량','재고수량','판매율'].map(label => (
                          <div key={label} style={{ ...labelCellStyle, background: labelBg, color: '#6B7280' }}>{label}</div>
                        ))}
                      </div>
                      {/* 스크롤 영역 (이미지 + 테이블) */}
                      <style>{`.sales-color-table::-webkit-scrollbar{height:4px}.sales-color-table::-webkit-scrollbar-thumb{background:${salesIsFemale ? '#f9a8c9' : '#CCC5B9'};border-radius:2px}.sales-color-table::-webkit-scrollbar-track{background:transparent}`}</style>
                      <div ref={scrollTableRef} className="nodrag nopan sales-color-table" style={{ flex: 1, overflowX: needsScroll ? 'auto' : 'visible', overflowY: 'visible', scrollbarWidth: needsScroll ? 'thin' : 'none', scrollbarColor: `${salesIsFemale ? '#f9a8c9' : '#CCC5B9'} transparent` }}>
                        <table style={{ width: '100%', minWidth: tableMinWidth, tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: '1px 0', fontSize: 8 }}>
                          <thead>
                            {/* 이미지 행 */}
                            <tr style={{ height: imgH }}>
                              {allSameImage ? (
                                <td colSpan={salesData.length} style={{ padding: 0, textAlign: 'center', verticalAlign: 'middle' }}>
                                  {singleImgUrl && <img src={singleImgUrl} alt={part_cd} loading="lazy" decoding="async" style={{ display: 'block', height: imgH, maxWidth: '100%', objectFit: 'contain', margin: '0 auto' }} />}
                                </td>
                              ) : (
                                salesData.map((item, idx) => {
                                  const src = item.image || fallbackImageUrl || null;
                                  return (
                                    <td key={idx} style={{ padding: 0, textAlign: 'center', verticalAlign: 'middle' }}>
                                      {src && <img src={src} alt={item.color_cd} loading="lazy" decoding="async" style={{ display: 'block', height: imgH, width: '100%', objectFit: 'contain', margin: '0 auto' }} />}
                                    </td>
                                  );
                                })
                              )}
                            </tr>
                            {/* 컬러 헤더 행 */}
                            <tr style={{ height: H }}>
                              {salesData.map((item, idx) => (
                                <th key={idx} style={{ ...cellStyle, background: headerBg, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {item.color_cd || `컬러${idx + 1}`}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {[
                              salesData.map((item, idx) => <td key={idx} style={{ ...cellStyle, textAlign: 'center', color: '#6B7280' }}>{item.ac_ord_qty_kor?.toLocaleString() || '-'}</td>),
                              salesData.map((item, idx) => <td key={idx} style={{ ...cellStyle, textAlign: 'center' }}>{item.ac_stor_qty_kor?.toLocaleString() || '-'}</td>),
                              salesData.map((item, idx) => <td key={idx} style={{ ...cellStyle, textAlign: 'center' }}>{item.ac_sale_qty_cns?.toLocaleString() || '-'}</td>),
                              salesData.map((item, idx) => <td key={idx} style={{ ...cellStyle, textAlign: 'center' }}>{item.stock_qty?.toLocaleString() || '-'}</td>),
                              salesData.map((item, idx) => <td key={idx} style={{ ...cellStyle, textAlign: 'center' }}>{item.sale_rate ? `${item.sale_rate}%` : '-'}</td>),
                            ].map((cells, i) => (
                              <tr key={i} style={{ height: H }}>{cells}</tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  );
                })()}
              </div>
            ) : (
              <div style={{ padding: "2px 4px 2px", fontSize: 8 }}>
                <div style={{ height: 60, flexShrink: 0 }} />{/* 이미지 자리 빈 공간 */}
                <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "separate", borderSpacing: "1px 0", fontSize: 8 }}>
                  <colgroup>
                    <col style={{ width: 44 }} />
                    <col /><col /><col /><col />
                  </colgroup>
                  <thead>
                    <tr style={{ height: 14 }}>
                      <th style={{ background: "rgb(238 235 230)" }} />
                      {['컬러1','컬러2','컬러3','컬러4'].map(c => (
                        <th key={c} style={{ background: "rgb(238 235 230)", color: '#9CA3AF', padding: "2px 2px", fontSize: 8, lineHeight: '8px', textAlign: "center", fontWeight: 400 }}>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {['발주수량','입고수량','판매수량','재고수량','판매율'].map(label => (
                      <tr key={label} style={{ height: 14 }}>
                        <td style={{ background: "rgb(250 249 247)", color: '#6B7280', padding: '2px 3px', fontSize: 8, lineHeight: '8px', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', borderBottom: '1px solid #e5e7eb' }}>{label}</td>
                        {[0,1,2,3].map(i => <td key={i} style={{ fontSize: 8, lineHeight: '8px', borderBottom: '1px solid #e5e7eb' }} />)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            </div>{/* 이미지+컬러 스크롤 영역 끝 */}
          </div>
        </div>

      </>
    );
  }

  // 레인지맵 레이아웃
  if (mapType === '레인지맵') {
    return (
      <>
        <div ref={nodeRef} style={{ width: width || 260, height: height || 260, position: "relative" }}>

          <div style={{ border: selected ? "3px solid #A89F91" : "1px solid #ccc5b9", borderRadius: 8, background: "#fff", fontFamily: "Inter, ui-sans-serif", overflow: "hidden", boxSizing: "border-box", width: "100%", height: "100%" }}>
            {/* 헤더: 스타일품번 | 소재명 */}
            <div onClick={() => { if (onSelectNode) onSelectNode(); }} style={{ display: "flex", justifyContent: "space-between", padding: "6px 8px", background: "#CCC5B94F", fontSize: 10 }}>
              {isEditingPartCode ? (
                <input
                  ref={partCodeInputRef}
                  type="text"
                  className="nodrag nopan"
                  value={inputPartCode}
                  onChange={handlePartCodeChange}
                  onBlur={handleRangeMapPartCodeBlur}
                  onKeyDown={handleRangeMapPartCodeKeyDown}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  autoFocus
                  style={{
                    width: '80px',
                    padding: '2px 4px',
                    border: '1px solid #9CA3AF',
                    borderRadius: 4,
                    fontSize: 12,
                    fontWeight: 700,
                    outline: 'none',
                    pointerEvents: 'auto'
                  }}
                />
              ) : (
                <span
                  className="nodrag nopan"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    // 다른 필드 편집 중이면 먼저 저장 후 종료
                    if (editingField && onUpdateField) {
                      onUpdateField(nodeId, editingField, editingValue);
                      setEditingField(null);
                      setEditingValue('');
                    }
                    if (onSelectNode && !selected) onSelectNode();
                    setIsEditingPartCode(true);
                    if (onUpdateEditingState) {
                      onUpdateEditingState(true);
                    }
                  }}
                  style={{
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontWeight: 700,
                    backgroundColor: part_cd && part_cd.length >= 2 && part_cd[1] === 'F' ? '#f9cade' : 'transparent',
                    pointerEvents: 'auto'
                  }}
                  title="클릭하여 스타일 코드 입력"
                >
                  {part_cd || '스타일품번'}
                </span>
              )}
              <EditableField fieldName="fabric" value={fabric} placeholder="소재명" />
            </div>

            {/* 스타일명 */}
            <div onClick={(e) => e.stopPropagation()} style={{ padding: "0px 8px 6px 8px", textAlign: "center", borderBottom: "1px solid #E5E7EB" }}>
              <EditableField fieldName="description" value={description} placeholder="스타일명" style={{ fontSize: 10, color: description ? "#6B7280" : "#9CA3AF" }} />
            </div>

            {/* 도메인 | 서브도메인 | 라이프스타일 */}
            <div onClick={() => { if (onSelectNode) onSelectNode(); }} style={{ display: "flex", justifyContent: "space-between", padding: "8px", fontSize: 10 }}>
              <EditableField fieldName="domain" value={domain} placeholder="도메인" />
              <EditableField fieldName="sub_domain" value={sub_domain} placeholder="서브도메인" />
              <EditableField fieldName="life_style" value={life_style} placeholder="라이프스타일" />
            </div>

            {/* 이미지 */}
            <div style={{ display: "flex", justifyContent: "center", minHeight: 90, padding: "3px" }} onClick={() => {
              if (onSelectNode) onSelectNode();
              setSelectedNode(data);
            }}>
              {image && !imageError ? (
                <img
                  src={image}
                  alt={description || `img-${description}`}
                  style={{ width: 240, height: 90, objectFit: "contain" }}
                  onError={() => setImageError(true)}
                />
              ) : imageError && fallbackImageUrl && !fallbackError ? (
                <img
                  src={fallbackImageUrl}
                  alt={description || `img-${description}`}
                  style={{ width: 240, height: 90, objectFit: "contain" }}
                  onError={() => setFallbackError(true)}
                />
              ) : (
                <div style={{ width: 250, height: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', border: "1px dashed #D1D5DB", borderRadius: 4, color: '#9CA3AF', fontSize: 12 }}>
                  이미지
                </div>
              )}
            </div>

            {/* 컬러수 | 판매가 | 예상수량 */}
            <div onClick={() => { if (onSelectNode) onSelectNode(); }} style={{ display: "flex", justifyContent: "space-between", padding: "4px 8px", fontSize: 10 }}>
              <EditableField fieldName="color_count" value={color_count} placeholder="컬러수" />
              <EditableField fieldName="price" value={price ? String(price) : ''} placeholder="판매가" />
              <EditableField fieldName="expected_qty" value={expected_qty} placeholder="예상수량" />
            </div>
          </div>
        </div>

      </>
    );
  }

  // 기본 레이아웃 (CAD맵, 판매맵, 런칭맵 등)
  const isFemale = part_cd && part_cd.length >= 2 && (part_cd[1] === 'F' || part_cd[1] === 'W');
  const cardBorder = isFemale
    ? (selected ? "3px solid #e879a3" : "1px solid #f9cade")
    : (selected ? "3px solid #A89F91" : "1px solid #ccc5b9");
  const headerBg = isFemale ? '#F9CADE4F' : '#CCC5B94F';

  return (
    <>
      <div ref={nodeRef} style={{ width: width || 260, height: height || 260, position: "relative" }}>
        <div style={{ border: cardBorder, borderRadius: 8, background: "#fff", fontFamily: "Inter, ui-sans-serif", overflow: "hidden", boxSizing: "border-box", width: "100%", height: "100%" }}>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 8px", background: headerBg, fontSize: 10 }}>
            {isEditingPartCode ? (
              <input
                ref={partCodeInputRef}
                type="text"
                className="nodrag nopan"
                value={inputPartCode}
                onChange={handlePartCodeChange}
                onBlur={handlePartCodeBlur}
                onKeyDown={handlePartCodeKeyDown}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                autoFocus
                style={{
                  width: '80px',
                  padding: '2px 4px',
                  border: '1px solid #9CA3AF',
                  borderRadius: 4,
                  fontSize: 12,
                  fontWeight: 700,
                  outline: 'none'
                }}
              />
            ) : (
              <span
                className="nodrag nopan"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onSelectNode) onSelectNode();

                  setIsEditingPartCode(true);
                  // 편집 시작 시 편집 상태 전송
                  if (onUpdateEditingState) {
                    onUpdateEditingState(true);
                  }
                }}
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontWeight: 700,
                }}
                title="클릭하여 스타일 코드 입력"
              >
                {part_cd || '스타일품번'}
              </span>
            )}
            <span style={{ color: fabric ? '#000' : '#9CA3AF', whiteSpace: 'nowrap' }}>{fabric || '소재명'}</span>
            {/* <span style={{ color: fiber_content ? '#000' : '#9CA3AF' }}>{fiber_content || '혼용률'}</span> */}
          </div>
          <div style={{ padding: "6px 8px" }}>
            <div style={{ fontSize: 10, color: description ? "#6B7280" : "#9CA3AF", textAlign: "center", whiteSpace: 'nowrap' }}>{description || '스타일명'}</div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 10 }}>
              <div style={{ color: domain ? '#000' : '#9CA3AF' }}>{domain || '도메인'}</div>
              {/* <div style={{ color: set_up_style_no ? '#000' : '#9CA3AF' }}>{set_up_style_no || '셋업 스타일 넘버'}</div> */}
              <div style={{ color: fit ? '#000' : '#9CA3AF' }}>{fit || '핏'}</div>
              <div style={{ color: price ? '#000' : '#9CA3AF' }}>{price ? `${price.toLocaleString()}` : '판매가'}</div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 10 }}>
              <div style={{ color: life_style ? '#000' : '#9CA3AF' }}>{life_style || '라이프스타일'}</div>
              <div style={{ color: level ? '#000' : '#9CA3AF' }}>{level || '레벨'}</div>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "center", minHeight: 90 }} onClick={() => {
            setSelectedNode(data);
          }}>
            {image && !imageError ? (
              <img
                src={image}
                alt={description || `img-${description}`}
                style={{ width: 240, height: 90, objectFit: "contain" }}
                onError={() => setImageError(true)}
              />
            ) : imageError && fallbackImageUrl && !fallbackError ? (
              <img
                src={fallbackImageUrl}
                alt={description || `img-${description}`}
                style={{ width: 240, height: 90, objectFit: "contain" }}
                onError={() => setFallbackError(true)}
              />
            ) : (
              <div style={{ width: 250, height: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', border: "1px dashed #D1D5DB", borderRadius: 4, color: '#9CA3AF', fontSize: 12 }}>
                이미지
              </div>
            )}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 8px", fontSize: 10, borderBottom: "1px solid #E5E7EB" }}>
            <span style={{ color: designer ? '#000' : '#9CA3AF' }}>{designer || '담당 디자이너'}</span>
            <span style={{ color: vendor ? '#000' : '#9CA3AF' }}>{vendor || '협력사'}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 8px", fontSize: 10 }}>
            {colors && colors.length > 0 ? (
              <div style={{ fontSize: 10 }}>
                <span style={{ color: "#374151" }}>
                  {colors.map((color) => {
                    return typeof color === 'object' ? JSON.stringify(color) : String(color);
                  }).join(', ')}
                </span>
              </div>
            ) : (
              <span style={{ color: '#9CA3AF' }}>컬러</span>
            )}
          </div>
        </div>
      </div>

    </>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.nodeId === nextProps.nodeId &&
    prevProps.selected === nextProps.selected &&
    prevProps.width === nextProps.width &&
    prevProps.height === nextProps.height &&
    prevProps.data?.part_cd === nextProps.data?.part_cd &&
    prevProps.data?.description === nextProps.data?.description &&
    prevProps.data?.fabric === nextProps.data?.fabric &&
    prevProps.data?.image === nextProps.data?.image &&
    prevProps.data?.mapType === nextProps.data?.mapType &&
    prevProps.data?.domain === nextProps.data?.domain &&
    prevProps.data?.sub_domain === nextProps.data?.sub_domain &&
    prevProps.data?.life_style === nextProps.data?.life_style &&
    prevProps.data?.price === nextProps.data?.price &&
    prevProps.data?.color_count === nextProps.data?.color_count &&
    prevProps.data?.expected_qty === nextProps.data?.expected_qty &&
    prevProps.data?.isLoading === nextProps.data?.isLoading &&
    prevProps.data?.tag_price === nextProps.data?.tag_price &&
    prevProps.data?.unit_cost === nextProps.data?.unit_cost &&
    prevProps.data?.ac_ord_qty_kor === nextProps.data?.ac_ord_qty_kor &&
    prevProps.data?.ac_sale_qty_cns === nextProps.data?.ac_sale_qty_cns &&
    prevProps.data?.style_sale_rate === nextProps.data?.style_sale_rate &&
    prevProps.data?.salesData === nextProps.data?.salesData &&
    prevProps.data?.colors === nextProps.data?.colors &&
    prevProps.data?.selectedBy === nextProps.data?.selectedBy &&
    prevProps.data?.showUserLabel === nextProps.data?.showUserLabel &&
    prevProps.onUpdateData === nextProps.onUpdateData &&
    prevProps.onUpdateField === nextProps.onUpdateField &&
    prevProps.onUpdateEditingState === nextProps.onUpdateEditingState &&
    prevProps.onSelectNode === nextProps.onSelectNode
  );
});

export default StyleCard;
